// A team's banner (PLAN-2026-10-24-team-banners): a picture behind the team
// on the screens about one team (My Team's header, each side of Game Day,
// Edit team's preview), chosen beside its colors and its badge. It took the
// place of a face's backdrop, which a badge's tight circle left no room to
// see (DIRECTION U25).
//
// DATA, NOT DRAWINGS, as a face's parts are: each banner's id, its name
// aloud, the days a seasonal one is offered and the achievement that earns an
// earned one. How a banner looks is a client's (`TeamBanner` in apps/web), so
// a second client draws the same scenes its own way.

import { z } from 'zod';
import type { TeamBadge } from './badges';

/** Days of the year, as month and day, both included; a span can run over New Year's. */
export interface BannerDays {
  readonly from: readonly [month: number, day: number];
  readonly to: readonly [month: number, day: number];
}

/** One banner a team can choose. */
export interface BannerChoice {
  readonly id: string;
  /** Its name on screen and aloud. */
  readonly name: string;
  /**
   * A seasonal banner's days and the tag the picker shows on it: offered on
   * those days, kept by a team that has it after them, and the scene
   * *Follow the seasons* shows on them.
   */
  readonly season?: { readonly tag: string; readonly days: BannerDays };
  /** The achievement that earns it, by its id in the achievements catalog. */
  readonly earnedBy?: string;
  /** What earns it, in the catalog's words, said beside it while it is not earned. */
  readonly earnedHow?: string;
}

const B = <I extends string>(id: I, name: string): BannerChoice & { readonly id: I } => ({
  id,
  name,
});
const seasonal = <P extends BannerChoice>(
  tag: string,
  from: BannerDays['from'],
  to: BannerDays['to'],
  part: P,
): P => ({ ...part, season: { tag, days: { from, to } } });
const earned = <P extends BannerChoice>(by: string, how: string, part: P): P => ({
  ...part,
  earnedBy: by,
  earnedHow: how,
});

/**
 * Every banner, in the order the picker shows them. `seasons` is *Follow the
 * seasons*, which draws no scene of its own: it shows the seasonal banner
 * whose days it is, and the football field between them (`bannerSceneOn`).
 * The ids a face's backdrop had are kept, so migration 0085 carries a
 * backdrop over as it is.
 */
export const TEAM_BANNERS = [
  B('seasons', 'Follow the seasons'),
  B('confetti', 'Confetti'),
  B('stars', 'Starry night'),
  B('stripes', 'Racing stripes'),
  B('sunburst', 'Sunburst'),
  B('dots', 'Halftone dots'),
  B('checks', 'Checkered flag'),
  B('field', 'Football field'),
  B('lights', 'Stadium lights'),
  B('skyline', 'City skyline'),
  B('mountains', 'Mountains'),
  B('sunset', 'Beach sunset'),
  earned('playoff-bound', 'Make the playoffs.', B('spotlight', 'Spotlight')),
  seasonal('Halloween', [10, 1], [11, 2], B('bats', 'Halloween night')),
  seasonal('Fall', [11, 3], [11, 30], B('leaves', 'Autumn leaves')),
  seasonal('Winter', [12, 1], [1, 31], B('snow', 'Snowy hills')),
  seasonal("Valentine's", [2, 1], [2, 15], B('hearts', 'Hearts')),
  seasonal("St. Pat's", [3, 10], [3, 18], B('clovers', 'Rainbow and clovers')),
] as const satisfies readonly BannerChoice[];

export type TeamBannerId = (typeof TEAM_BANNERS)[number]['id'];
/** A scene a banner draws: every banner but `seasons`, which shows one of the others. */
export type BannerScene = Exclude<TeamBannerId, 'seasons'>;

/**
 * A team's banner, on the wire and as stored (`teams.banner`, 0085): one of
 * the catalog's ids. None is null, as a badge's initials are.
 */
export const TeamBanner = z.enum(
  TEAM_BANNERS.map((b) => b.id) as [TeamBannerId, ...TeamBannerId[]],
);

/** A banner by id, or undefined for one the catalog does not have. */
export function teamBanner(id: string): BannerChoice | undefined {
  return (TEAM_BANNERS as readonly BannerChoice[]).find((b) => b.id === id);
}

/** Whether a day falls in a span of days, by the day's own month and date where it is read. */
function within(days: BannerDays, on: Date): boolean {
  const day = (on.getMonth() + 1) * 100 + on.getDate();
  const from = days.from[0] * 100 + days.from[1];
  const to = days.to[0] * 100 + days.to[1];
  return from <= to ? day >= from && day <= to : day >= from || day <= to;
}

/**
 * Whether the picker offers a banner on a day: an everyday one always, a
 * seasonal one on its days, an earned one to its earner (`earned`, the
 * achievements they hold), and the one the team has whatever it is, so a
 * banner chosen in October stays chosen, and shown, in December.
 */
export function bannerOffered(
  banner: BannerChoice,
  on: Date,
  chosen = false,
  earnedIds: readonly string[] = [],
): boolean {
  if (chosen) return true;
  if (banner.earnedBy !== undefined) return earnedIds.includes(banner.earnedBy);
  return banner.season === undefined || within(banner.season.days, on);
}

/** The seasonal scene for a day, or null between the seasons. */
export function seasonalSceneOn(on: Date): BannerScene | null {
  for (const b of TEAM_BANNERS as readonly BannerChoice[])
    if (b.season !== undefined && within(b.season.days, on)) return b.id as BannerScene;
  return null;
}

/** What *Follow the seasons* shows between them. */
export const BETWEEN_SEASONS: BannerScene = 'field';

/**
 * Each bot's banner between the seasons (AI_MANAGERS.md §5.4), by the
 * character's id, to fit who it is: every bot follows the seasons, and these
 * are what it shows the rest of the year. A character with none shows the
 * football field.
 */
export const BOT_BANNERS: Readonly<Record<string, BannerScene>> = {
  'captain-hype': 'sunburst',
  'chaos-gremlin': 'confetti',
  'coach-bot': 'field',
  'grandma-dot': 'dots',
  'lucky-lou': 'stars',
  'midnight-mike': 'skyline',
  'sunday-sam': 'sunset',
  'the-accountant': 'checks',
  'the-intern': 'stripes',
  'the-professor': 'mountains',
  'uncle-rico': 'lights',
  'waiver-wendy': 'sunset',
};

/**
 * The scene a team's banner shows on a day, or null for none: the team's own
 * banner, *Follow the seasons* through the calendar; for a seat a bot holds
 * with none of its own, the bot's (the season's, or its own between them).
 * Read from the device's own date, as a seasonal face part is, so every
 * client shows the same scene on the same day.
 */
export function bannerSceneOn(
  team: { banner?: TeamBannerId | null; badge?: TeamBadge },
  on: Date,
): BannerScene | null {
  const chosen = team.banner ?? null;
  if (chosen === 'seasons') return seasonalSceneOn(on) ?? BETWEEN_SEASONS;
  if (chosen !== null) return chosen;
  const badge = team.badge ?? null;
  if (badge?.kind === 'bot') return seasonalSceneOn(on) ?? BOT_BANNERS[badge.id] ?? BETWEEN_SEASONS;
  return null;
}

/**
 * The achievement a banner needs its saver to have earned, or none: an earned
 * banner newly chosen asks for its achievement; one the team has already is
 * kept, as an earned face part is.
 */
export function bannerNeeds(
  banner: TeamBannerId | null,
  kept: TeamBannerId | null = null,
): string[] {
  if (banner === null || banner === kept) return [];
  const by = teamBanner(banner)?.earnedBy;
  return by === undefined ? [] : [by];
}

/** The banners earned by playing, by id: what a seat changing hands gives up. */
export function earnedBanners(): TeamBannerId[] {
  return (TEAM_BANNERS as readonly BannerChoice[])
    .filter((b) => b.earnedBy !== undefined)
    .map((b) => b.id as TeamBannerId);
}
